/**
 * Profielrooster — wie gebruiken dit apparaat?
 *
 * ─── WAT HIER WEL EN NIET IN STAAT ──────────────────────────────────────────
 *
 * Alleen wat nodig is om een tegel te tonen en het inlogveld voor te vullen:
 * naam, avatar, gebruikersnaam, namespace, rol. Een tegel is een SNELKOPPELING,
 * geen sleutel.
 *
 * Wat hier bewust NIET in staat:
 *   - wachtwoorden of vernieuwingstokens
 *   - voortgang, streaks, sterren, toetsuitslagen
 *
 * Waarom geen opgeslagen sessies — de doorslaggevende reden is niet de sterkte
 * van een pincode maar tokenrotatie: Supabase roteert vernieuwingstokens, en
 * een bewaard token dat na rotatie opnieuw wordt aangeboden geldt als
 * hergebruik. Dat kan de hele tokenfamilie ongeldig maken, waarna een kind op
 * ÁLLE apparaten uitgelogd raakt zonder aanwijsbare aanleiding. Die storing is
 * niet uit te leggen aan een ouder. Het kind typt dus zijn wachtwoord — vanaf
 * groep 6 is dat een redelijke vraag.
 *
 * Bijkomend voordeel: een elders gewijzigd wachtwoord werkt hier gewoon.
 *
 * ─── OPT-IN ─────────────────────────────────────────────────────────────────
 *
 * Het rooster zelf is een verwerking: op een klassikaal apparaat verraadt een
 * rij namen wie er in die klas zit. Daarom staat het standaard UIT en zet de
 * gebruiker het per apparaat aan. Bij een schoolaccount blijft het uit tenzij
 * iemand het bewust aanzet.
 *
 * @see docs/specs/PROFILE_SWITCHER_DESIGN.md
 */

import { warn } from '../../utils/logger'

const ROSTER_KEY = 'meester_djay_device_roster'
const ENABLED_KEY = 'meester_djay_device_roster_enabled'

/** Maximaal aantal tegels. Meer dan dit is geen gezinsapparaat meer. */
const MAX_PROFILES = 6

export interface RosterProfile {
  id: string
  displayName: string
  role: 'learner' | 'parent' | 'teacher' | 'admin'
  /** Voor leerlingen: het inlogveld dat we voorvullen. */
  username: string | null
  /**
   * Sinds 0005 is `username` uniek per namespace, niet globaal. Zonder dit
   * veld vult een tegel de verkeerde gebruikersnaam in bij een naamgenoot op
   * een andere school.
   */
  usernameNamespace: string | null
  /** Voor volwassenen: het e-mailadres. Nooit voor leerlingen. */
  email: string | null
  avatar: string | null
  lastUsedAt: number
}

export function isRosterEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true'
  } catch {
    return false
  }
}

export function setRosterEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, String(enabled))
    // Uitzetten betekent ook opruimen. Een uitgeschakeld rooster dat de namen
    // nog bewaart is geen uitgeschakeld rooster.
    if (!enabled) localStorage.removeItem(ROSTER_KEY)
  } catch (err) {
    warn('[Roster] Voorkeur opslaan mislukt', err)
  }
}

export function getRoster(): RosterProfile[] {
  if (!isRosterEnabled()) return []
  try {
    const raw = localStorage.getItem(ROSTER_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as RosterProfile[]
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((p) => typeof p?.id === 'string')
      .sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))
  } catch {
    return []
  }
}

function persist(profiles: RosterProfile[]): void {
  try {
    localStorage.setItem(ROSTER_KEY, JSON.stringify(profiles))
  } catch (err) {
    // Vol geheugen mag nooit het inloggen blokkeren — het rooster is comfort,
    // geen functionaliteit.
    warn('[Roster] Opslaan mislukt', err)
  }
}

/**
 * Leg vast dat dit profiel dit apparaat gebruikt.
 *
 * Aangeroepen na een GESLAAGDE inlog, nooit eerder: een mislukte poging mag
 * geen naam achterlaten, anders wordt het rooster een manier om te achterhalen
 * welke accounts bestaan.
 */
export function rememberProfile(
  profile: Omit<RosterProfile, 'lastUsedAt'>,
): void {
  if (!isRosterEnabled()) return
  const existing = getRoster().filter((p) => p.id !== profile.id)
  const next = [{ ...profile, lastUsedAt: Date.now() }, ...existing].slice(
    0,
    MAX_PROFILES,
  )
  persist(next)
}

/** Haal één tegel weg. De lokale leerdata blijft staan — dat is een aparte keuze. */
export function forgetProfile(id: string): void {
  persist(getRoster().filter((p) => p.id !== id))
}

export function clearRoster(): void {
  try {
    localStorage.removeItem(ROSTER_KEY)
  } catch (err) {
    warn('[Roster] Legen mislukt', err)
  }
}
